import {
  Typography,
  Container,
  Link,
  Box,
  LinearProgress,
  Button,
  Grid2 as Grid,
  Link as MuiLink,
} from '@mui/material';
import { FC, useState, Dispatch, SetStateAction, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthPasswordResetDto } from '@libs/dto';
import { ApiError } from '@libs/types';
import { FormProvider, useForm, useSnackbar } from '@carcareers/contexts';
import { TextInput, OTPInput, PasswordInput } from '@carcareers/components';
import {
  useAuthRequestPasswordResetMutation,
  useAuthVerifyPasswordResetOtpMutation,
  useAuthResetPasswordMutation,
} from '@carcareers/store';

export const ForgotPassword: FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [searchParams] = useSearchParams();
  const { isMigrated, isExternalApply } = useLocation().state ?? { isMigrated: false, isExternalApply: false };

  const email = searchParams.get('email')?.toLowerCase();

  const steps = ['Enter Your Email', 'Verify Your Email', 'Reset Password'];

  const Header = () => {
    if (isMigrated) {
      return (
        <>
          <Typography component="h2" variant="h3" align="center" my={3} color="primary.main">
            Welcome back to CARCAREERS.COM!
          </Typography>
          <Typography component="h2" variant="body2" align="center" my={3}>
            Things have changed a bit and we need you to reset your password to continue.
          </Typography>
        </>
      );
    } else if (isExternalApply) {
      return (
        <>
          <Typography component="h2" variant="h3" align="center" my={3} color="primary.main">
            Welcome to CARCAREERS.COM!
          </Typography>
          <Typography component="h2" variant="body1" align="center" my={3}>
            Our records show that you have applied to a job on CARCAREERS.COM through one of our partners. Please create
            a password to continue.
          </Typography>
          <Typography component="p" variant="subtitle1" align="center" my={3}>
            If you have any questions, please contact support at{' '}
            <Link
              component={MuiLink}
              href="mailto:info@carcareers.com"
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:active': { color: 'primary.dark' },
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              info@carcareers.com
            </Link>
          </Typography>
        </>
      );
    } else {
      return (
        <>
          <Typography component="h2" variant="body1" align="center" my={3}>
            Remember your password?{' '}
            <Link
              component={RouterLink}
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:active': { color: 'primary.dark' },
                '&:hover': { textDecoration: 'underline' },
              }}
              to="/auth/login"
            >
              Sign in here
            </Link>
            .
          </Typography>
          <Typography component="h1" variant="h3" align="center" my={3}>
            Reset Your Password
          </Typography>
        </>
      );
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Header />

      <FormProvider initialData={{ email }}>
        <ForgotPasswordStepper activeStep={activeStep} setActiveStep={setActiveStep} steps={steps} />
      </FormProvider>
    </Container>
  );
};

interface ForgotPasswordStepperProps {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  steps: string[];
  email?: string;
}

const ForgotPasswordStepper: FC<ForgotPasswordStepperProps> = ({ activeStep, setActiveStep, steps }) => {
  const { errors } = useForm();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', p: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(((activeStep + 1) / steps.length) * 100)}% Complete
          </Typography>
        </Box>
        <LinearProgress
          color={errors.length > 0 ? 'error' : 'primary'}
          variant="determinate"
          value={((activeStep + 1) / steps.length) * 100}
          sx={{
            height: 10,
            borderRadius: 5,
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              backgroundColor: errors.length > 0 ? 'error.main' : 'primary.main',
            },
          }}
        />
      </Box>

      {activeStep === 0 && <EmailStep />}
      {activeStep === 1 && <VerifyOtpStep />}
      {activeStep === 2 && <ResetPasswordStep />}

      <ForgotPasswordStepNav activeStep={activeStep} setActiveStep={setActiveStep} />
    </Box>
  );
};

const EmailStep: FC = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        We'll send you a 6-digit code.
      </Typography>
      <TextInput required label="Email" name="email" type="email" autoComplete="email" fullWidth />
    </Box>
  );
};

const VerifyOtpStep: FC = () => {
  const { data } = useForm<AuthPasswordResetDto>();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
        Verify Your Email
      </Typography>
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        We've sent a 6-digit code to <strong>{data?.email}</strong>.<br />
        Please enter it below to verify your email address.
      </Typography>
      <OTPInput name="otp" length={6} />
    </Box>
  );
};

const ResetPasswordStep: FC = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
        Create New Password
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextInput required label="Email" name="email" type="email" autoComplete="email" disabled fullWidth />
        </Grid>
      </Grid>
      <PasswordInput />
    </Box>
  );
};

interface ForgotPasswordStepNavProps {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}

const ForgotPasswordStepNav: FC<ForgotPasswordStepNavProps> = ({ activeStep, setActiveStep }) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data, setErrors, errors } = useForm<AuthPasswordResetDto>();
  const [requestPasswordReset] = useAuthRequestPasswordResetMutation();
  const [verifyPasswordResetOtp] = useAuthVerifyPasswordResetOtpMutation();
  const [resetPassword] = useAuthResetPasswordMutation();

  const clearErrorsForCurrentStep = () => {
    const stepErrorMap = {
      0: ['email'],
      1: ['otp'],
      2: ['password', 'confirmPassword'],
    };

    const fieldsForStep = stepErrorMap[activeStep as keyof typeof stepErrorMap] || [];
    const filteredErrors = errors.filter(
      (error) => !fieldsForStep.some((field) => error.property === field || error.property.startsWith(`${field}.`)),
    );
    setErrors(filteredErrors);
  };

  useEffect(() => {
    if (activeStep === 0) {
      const isValid = !!data?.email;
      setIsFormValid(isValid);
    } else if (activeStep === 1) {
      const isValid = !!(data?.otp && data.otp.length === 6);
      setIsFormValid(isValid);
    } else if (activeStep === 2) {
      const isValid = !!(data?.password && data?.confirmPassword);
      setIsFormValid(isValid);
    }
  }, [activeStep, data]);

  const handleNext = async () => {
    setLoading(true);
    clearErrorsForCurrentStep();

    if (activeStep === 0) {
      const requestData = {
        email: data?.email?.toLowerCase(),
      };

      const { error } = await requestPasswordReset(requestData);

      if (!error) {
        setActiveStep((prevStep) => prevStep + 1);
        toggleSnackbar('success', 'Password reset code sent to your email.');
      } else {
        const e = error as unknown as { data: { errors: ApiError[] } };
        if (e?.data?.errors) {
          setErrors(e.data.errors);
        }
        toggleSnackbar('error', 'Failed to send password reset email. Please try again.');
      }

      setLoading(false);
    } else if (activeStep === 1) {
      const requestData = {
        email: data?.email?.toLowerCase(),
        otp: data?.otp,
      };

      const { error } = await verifyPasswordResetOtp(requestData);

      if (!error) {
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        const e = error as unknown as { data: { errors: ApiError[] } };
        if (e?.data?.errors) {
          setErrors(e.data.errors);
        } else {
          setErrors([{ property: 'otp', errors: ['Invalid OTP. Please try again.'] }]);
        }
      }

      setLoading(false);
    } else if (activeStep === 2) {
      const { error } = await resetPassword(data);

      if (!error) {
        navigate('/auth/login', {
          state: {
            passwordReset: true,
            email: data?.email,
          },
        });
        toggleSnackbar('success', 'Password reset successfully. Please log in with your new password.');
      } else {
        const e = error as unknown as { data: { errors: ApiError[] } };
        if (e?.data?.errors) {
          setErrors(e.data.errors);
        }
        toggleSnackbar('error', 'Failed to reset password. Please try again.');
      }

      setLoading(false);
    }
  };

  const handleBack = () => {
    clearErrorsForCurrentStep();

    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
      <Button onClick={handleBack} disabled={loading}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>
      <Button variant="contained" onClick={handleNext} disabled={!isFormValid || loading} loading={loading}>
        {activeStep === 2 ? 'Reset Password' : 'Next'}
      </Button>
    </Box>
  );
};

export default ForgotPassword;
